/** Render (/renders/:renderId): frame-step preview, download, contact sheet, lint, log tail + Retry, QA buttons for
 * every category + note, verdict history, Next unreviewed (`GET /api/work/next?queue=qa`, excluding this render).
 * No scheduling here. URL: t (playhead, ms), rate (playback rate). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, mediaDownloadUrl, mediaUrl, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { DownloadVideo } from "../../components/DownloadVideo";
import { ErrorBox } from "../../components/ErrorBox";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
import { errorText, Loading, Steps, useLiveInvalidate, when, WriteError } from "../common";
import { PlayerStatus, Transport, VideoSurface } from "../player/Player";
import { useMediaInfo, usePlayer, useUrlPlayerSync } from "../player/usePlayer";
import { qaLabel, type RenderDetail } from "../types";
import { useUrlState } from "../url";
import { CheckLoop, ParamsLine } from "./CheckLoop";
import { LintTable } from "./LintTable";
import { QaForm } from "./QaForm";

const STATUS_STATE: Record<string, string> = { queued: "queued", running: "running", done: "done", failed: "failed",
  cancelled: "skipped", stale: "stale" };

export function RenderView() {
  const { renderId } = useParams();
  const id = Number(renderId);
  const q = useQuery({
    queryKey: qk.entity("render", id),
    enabled: Number.isInteger(id) && id > 0,
    queryFn: () => unwrap(api.GET("/api/renders/{render_id}", { params: { path: { render_id: id } } })),
  });
  useLiveInvalidate((e) => e.entity === "render" && e.entity_id === id, [qk.entity("render", id)]);
  if (q.isPending) return <><PageHeader title={`Render #${id}`} /><Loading what="render" /></>;
  if (q.isError) return <section><PageHeader title={`Render #${id}`} md={{ page: "render", params: { id } }} /><ErrorBox error={q.error} /></section>;
  return <RenderLoaded r={q.data} />;
}

function NextUnreviewed({ current }: { current: number }) {
  const nav = useNavigate();
  const announce = useAnnounce();
  const next = useMutation({
    mutationFn: () => unwrap(api.GET("/api/work/next", { params: { query: { queue: "qa", exclude: String(current) } } })),
    onSuccess: (w) => {
      if (w.none_left || !w.item) announce("No unreviewed renders left");
      else {
        announce(`Next unreviewed: render #${w.item.item_id} (${w.remaining} left)`);
        nav(`/renders/${w.item.item_id}`);
      }
    },
    onError: (e) => announce(`Next unreviewed failed: ${errorText(e)}`, "error"),
  });
  return (
    <button type="button" data-testid="next-unreviewed" disabled={next.isPending} onClick={() => next.mutate()}>
      Next unreviewed
    </button>
  );
}

function RenderLoaded({ r }: { r: RenderDetail }) {
  const url = useUrlState();
  const qc = useQueryClient();
  const announce = useAnnounce();
  const media = useMediaInfo(r.output_media_id);
  const fps = media.data?.fps ?? (typeof r.params.fps === "number" ? r.params.fps : 30);
  const { ctrl, videoRef } = usePlayer({ mediaKey: r.output_media_id ?? null, fps, durationMs: media.data?.durationMs ?? null,
    frameCount: media.data?.frames ?? null });
  useUrlPlayerSync(ctrl, url);
  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.entity("render", r.id) });
  const action = useMutation({
    mutationFn: (kind: "retry" | "cancel") => kind === "retry"
      ? unwrap(api.POST("/api/renders/{render_id}/retry", { params: { path: { render_id: r.id } } }))
      : unwrap(api.POST("/api/renders/{render_id}/cancel", { params: { path: { render_id: r.id } } })),
    onSuccess: (out, kind) => {
      announce(kind === "retry" ? `Retry queued for render #${out.id}` : `Cancelled render #${out.id}`);
      invalidate();
    },
    onError: (e, kind) => announce(`${kind === "retry" ? "Retry" : "Cancel"} failed: ${errorText(e)}`, "error"),
  });
  const canRetry = r.status === "failed" || r.status === "cancelled";  // a stale render is chosen again, not retried
  const failed = r.status === "failed" || r.status === "cancelled" || Boolean(r.error);
  return (
    <section className="ws8-page" data-testid="render-page">
      <PageHeader title={`Render #${r.id}: ${r.template_id}`} md={{ page: "render", params: { id: r.id } }}>
        <NextUnreviewed current={r.id} />
        <DownloadVideo mediaId={r.output_media_id} />
      </PageHeader>
      <p className="ws8-row" data-testid="render-summary">
        <StatusPill state={STATUS_STATE[r.status] ?? r.status} label={r.status} />
        {r.progress != null && r.status === "running" && <span>{Math.round(r.progress * 100)}%</span>}
        {r.eta_s != null && <span>ETA {r.eta_s} s</span>}
        {r.queue_position != null && <span>queue #{r.queue_position}</span>}
        <span className="ws8-muted">requested by {r.requested_by} · queued {when(r.queued_at)}
          {r.finished_at ? ` · finished ${when(r.finished_at)}` : ""}</span>
        <span className={`ws8-badge ${r.approved ? "ws8-badge-ok" : ""}`}>{r.approved ? "approved" : "not approved"}</span>
        <span>QA: {qaLabel(r.qa_category)}{r.qa_actor ? ` by ${r.qa_actor}` : ""}</span>
        <Link to={`/clips/${r.clip_id}`}>Clip #{r.clip_id}</Link>
        {r.video_id != null && <Link to={`/watch/${r.video_id}`}>Watch the MV</Link>}
        {r.legacy && <span className="ws8-badge">legacy</span>}
      </p>
      <ParamsLine params={r.params} />
      <Steps steps={r.steps} />
      <div className="ws8-render">
        <div className="ws8-player-col">
          <VideoSurface ctrl={ctrl} videoRef={videoRef} mediaId={r.output_media_id} webmId={r.preview_webm_media_id}
            label={`Render #${r.id} output`}
            vcodec={media.data?.vcodec} />
          {r.output_media_id != null && (
            <>
              <PlayerStatus ctrl={ctrl} />
              <Transport ctrl={ctrl} rateParam={(x) => url.set({ rate: x === 1 ? null : x })} />
            </>
          )}
        </div>
        <div className="ws8-player-col">
          {r.status === "done" ? <QaForm render={r} /> : (
            <p className="ws8-muted" data-testid="qa-unavailable">
              {r.status === "stale" ? "This render is stale (its inputs changed): choose the template again; QA is closed." : `QA opens when the render has finished (${r.status}).`}
            </p>
          )}
          {(r.error || r.log_tail || canRetry) && (
            <section className="ws8-panel" aria-labelledby="ws8-log-h">
              <h2 id="ws8-log-h" className="ws8-h2">{failed ? "Failure" : "Render log"}</h2>
              {r.error && <p className="ws8-warn" data-testid="render-error">{r.error}</p>}
              {r.log_tail && <pre className="ws8-log" tabIndex={0} data-testid="log-tail" aria-label="Log tail">{r.log_tail}</pre>}
              <div className="ws8-row">
                {canRetry && <button type="button" data-testid="retry" disabled={action.isPending} onClick={() => action.mutate("retry")}>Retry</button>}
                {r.job_id != null && <Link to={`/jobs?job=${r.job_id}`}>Job #{r.job_id}</Link>}
              </div>
            </section>
          )}
          {(r.status === "queued" || r.status === "running") && (
            <button type="button" data-testid="cancel-render" disabled={action.isPending} onClick={() => action.mutate("cancel")}>
              Cancel render
            </button>
          )}
          <WriteError error={action.error} onReload={() => { action.reset(); invalidate(); }} />
        </div>
      </div>
      <section className="ws8-panel" aria-labelledby="ws8-sheet-h">
        <h2 id="ws8-sheet-h" className="ws8-h2">Contact sheet</h2>
        {r.sheet_media_id != null ? (
          <>
            <img className="ws8-sheet" src={mediaUrl(r.sheet_media_id)} alt={`Contact sheet of render #${r.id}`} data-testid="contact-sheet" />
            <p><a className="button" href={mediaDownloadUrl(r.sheet_media_id)} download data-testid="download-sheet">Download contact sheet</a></p>
          </>
        ) : <p className="ws8-muted">No contact sheet yet.</p>}
      </section>
      <section className="ws8-panel" aria-labelledby="ws8-lint-h">
        <h2 id="ws8-lint-h" className="ws8-h2">Lint</h2>
        <LintTable lint={Object.fromEntries(Object.entries(r.lint).filter(([k]) => k !== "qa"))} />
      </section>
      <section className="ws8-panel" aria-labelledby="ws8-qa-check-h">
        <h2 id="ws8-qa-check-h" className="ws8-h2">Exact check</h2>
        <CheckLoop lint={r.lint} />
      </section>
      <section className="ws8-panel" aria-labelledby="ws8-verdicts-h">
        <h2 id="ws8-verdicts-h" className="ws8-h2">Verdict history</h2>
        {r.verdicts.length === 0 ? <p className="ws8-muted" data-testid="no-verdicts">No verdicts yet.</p> : (
          <table className="ws8-table" data-testid="verdicts">
            <thead><tr><th scope="col">When</th><th scope="col">Category</th><th scope="col">Actor</th><th scope="col">Applied</th><th scope="col">Note</th></tr></thead>
            <tbody>
              {r.verdicts.map((v) => (
                <tr key={v.id}>
                  <td>{when(v.t)}</td><td>{qaLabel(v.category)}</td><td>{v.actor} ({v.actor_kind})</td>
                  <td>{v.applied ? "yes" : "no (suggestion only)"}</td><td>{v.note ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </section>
  );
}
